import { collection, getDocs, query, where, orderBy, documentId } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Booking } from './firestore';

export interface DashboardMetrics {
  totalUsers: number;
  activeBookings: number;
  totalRevenue: number;
  completionRate: number;
  pendingBookings: number;
  completedBookings: number;
  cancelledBookings: number;
  technicians: number;
  services: number;
  revenueByServiceType: { [key: string]: number };
  bookingsByStatus: { [key: string]: number };
  recentBookings: any[];
}

export interface BookingTrendData {
  date: string;
  bookings: number;
  revenue: number;
  completed: number;
}

export interface ServicePopularityData {
  name: string;
  bookings: number;
  revenue: number;
}

export const getEnhancedDashboardMetrics = async (): Promise<DashboardMetrics> => {
  try {
    // Fetch all collections in parallel
    const [usersSnapshot, bookingsSnapshot, techniciansSnapshot, servicesSnapshot] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(query(collection(db, 'bookings'), orderBy('createdAt', 'desc'))),
      getDocs(query(collection(db, 'users'), where('role', '==', 'technician'))),
      getDocs(collection(db, 'serviceTypes')),
    ]);

    const totalUsers = usersSnapshot.size;
    const technicians = techniciansSnapshot.size;
    const services = servicesSnapshot.size;

    // Process bookings
    const bookings = bookingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Booking);
    const activeBookings = bookings.filter(b => b.status && ['confirmed', 'in_progress'].includes(b.status)).length;
    const pendingBookings = bookings.filter(b => b.status === 'pending').length;
    const completedBookings = bookings.filter(b => b.status === 'completed').length;
    const cancelledBookings = bookings.filter(b => b.status === 'cancelled').length;

    const totalBookings = bookings.length;
    const completionRate = totalBookings > 0 ? Math.round((completedBookings / totalBookings) * 100) : 0;

    // Calculate revenue (from completed bookings with paymentStatus = 'paid')
    const totalRevenue = bookings
      .filter(b => b.status === 'completed' && b.paymentStatus === 'paid')
      .reduce((sum, b) => sum + (b.amount || 0), 0);

    // Revenue by service type
    const revenueByServiceType: { [key: string]: number } = {};
    bookings
      .filter(b => b.status === 'completed' && b.paymentStatus === 'paid')
      .forEach(b => {
        const serviceType = b.serviceType || 'unknown';
        revenueByServiceType[serviceType] = (revenueByServiceType[serviceType] || 0) + (b.amount || 0);
      });

    // Bookings by status
    const bookingsByStatus: { [key: string]: number } = {};
    bookings.forEach(b => {
      const status = b.status || 'unknown';
      bookingsByStatus[status] = (bookingsByStatus[status] || 0) + 1;
    });

    // Recent bookings (last 10) with customer names resolved from users
    const recentSlice = bookings.slice(0, 10);
    const customerIds = Array.from(new Set(recentSlice.map(b => b.customerId).filter(Boolean))) as string[];
    let idToUser: Record<string, any> = {};
    if (customerIds.length > 0) {
      // Firestore allows up to 10 values in 'in' queries which matches our slice size
      const usersSnap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', customerIds)));
      usersSnap.forEach(d => { idToUser[d.id] = d.data(); });
    }
    const recentBookings = recentSlice.map(b => {
      const user = idToUser[b.customerId || ''] || {};
      const nameFromUser = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      const nameFromContact = [b.customerContact?.firstName, b.customerContact?.lastName].filter(Boolean).join(' ').trim();
      const customerName = nameFromUser || nameFromContact || user.email || 'Unknown';
      return {
        id: b.id,
        customerName,
        serviceType: b.serviceType,
        scheduledTime: b.scheduledTime || b.scheduledDate,
        status: b.status || 'unknown',
        amount: b.amount,
      };
    });

    return {
      totalUsers,
      activeBookings,
      totalRevenue,
      completionRate,
      pendingBookings,
      completedBookings,
      cancelledBookings,
      technicians,
      services,
      revenueByServiceType,
      bookingsByStatus,
      recentBookings,
    };
  } catch (error) {
    console.error('Error fetching enhanced dashboard metrics:', error);
    throw error;
  }
};

export const getBookingTrendData = async (days: number = 7): Promise<BookingTrendData[]> => {
  try {
    const bookingsSnapshot = await getDocs(query(collection(db, 'bookings'), orderBy('createdAt', 'desc')));
    const bookings = bookingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Booking);

    // Group by date
    const dateMap: { [key: string]: { bookings: number; revenue: number; completed: number } } = {};

    const now = new Date();
    const startDate = new Date();
    startDate.setDate(now.getDate() - days);

    bookings.forEach(b => {
      let bookingDate: Date;
      if (b.createdAt && typeof b.createdAt === 'object' && 'toDate' in b.createdAt) {
        bookingDate = (b.createdAt as any).toDate();
      } else if (b.createdAt) {
        bookingDate = new Date(b.createdAt as any);
      } else {
        return; // Skip if no date
      }
      
      if (bookingDate >= startDate) {
        const dateKey = bookingDate.toISOString().split('T')[0];
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = { bookings: 0, revenue: 0, completed: 0 };
        }
        dateMap[dateKey].bookings += 1;
        
        if (b.status === 'completed' && b.paymentStatus === 'paid') {
          dateMap[dateKey].revenue += b.amount || 0;
          dateMap[dateKey].completed += 1;
        }
      }
    });

    // Convert to array and sort by date
    return Object.entries(dateMap)
      .map(([date, data]) => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        bookings: data.bookings,
        revenue: data.revenue / 100, // Convert cents to dollars
        completed: data.completed,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  } catch (error) {
    console.error('Error fetching booking trend data:', error);
    throw error;
  }
};

export const getServicePopularityData = async (): Promise<ServicePopularityData[]> => {
  try {
    const bookingsSnapshot = await getDocs(collection(db, 'bookings'));
    const bookings = bookingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Booking);

    const serviceMap: { [key: string]: { bookings: number; revenue: number } } = {};

    bookings
      .filter(b => b.status === 'completed' && b.paymentStatus === 'paid')
      .forEach(b => {
        const serviceType = b.serviceType || 'unknown';
        if (!serviceMap[serviceType]) {
          serviceMap[serviceType] = { bookings: 0, revenue: 0 };
        }
        serviceMap[serviceType].bookings += 1;
        serviceMap[serviceType].revenue += b.amount || 0;
      });

    return Object.entries(serviceMap)
      .map(([name, data]) => ({
        name: name ? (name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' ')) : 'Unknown',
        bookings: data.bookings,
        revenue: data.revenue / 100, // Convert cents to dollars
      }))
      .sort((a, b) => b.revenue - a.revenue);
  } catch (error) {
    console.error('Error fetching service popularity data:', error);
    throw error;
  }
};

